<template>
  <div class="publications">
    <nav class="breadcrumb">
      <router-link class="breadcrumb__link" to="/">{{ breadcrumb.home }}</router-link>
      <span class="breadcrumb__separator">&gt;</span>
      <span class="breadcrumb__current">{{ breadcrumb.current }}</span>
    </nav>

    <!-- <header class="page-header">
      <h1>{{ intro.title }}</h1>
      <p>{{ intro.description }}</p>
    </header> -->

    <section v-if="modes.length || topics.length" class="controls">
      <div v-if="modes.length" class="controls__modes">
        <button
          v-for="mode in modes"
          :key="mode.id"
          type="button"
          class="mode-button"
          :class="{ 'mode-button--active': filterMode === mode.id }"
          @click="setFilterMode(mode.id)"
        >
          {{ mode.label }}
        </button>
      </div>
      <div v-if="topics.length" class="controls__topics">
        <button
          v-for="topic in topics"
          :key="topic"
          type="button"
          class="topic-chip"
          :class="{ 'topic-chip--active': isTopicActive(topic) }"
          @click="toggleTopic(topic)"
        >
          {{ topic }}
        </button>
      </div>
    </section>

    <section class="publication-list">
      <div class="publication-list__content">
        <div
          v-for="group in publicationGroups"
          :key="group.year"
          :id="yearAnchor(group.year)"
          :data-year="group.year"
          class="publication-year"
        >
          <div class="publication-year__header">
            <span class="publication-year__bar" aria-hidden="true"></span>
            <h3 class="publication-year__title">{{ group.year }}</h3>
          </div>
          <article
            v-for="publication in group.items"
            :key="publication.id"
            class="publication-card"
          >
            <div class="publication-card__aside">
              <span class="publication-card__badge" :style="badgeStyle(publication.type)">
                {{ publication.type }}
              </span>
              <router-link
                v-if="publication.thumbnail"
                :to="publicationRoute(publication)"
                :class="[
                  'publication-card__media',
                  {
                    'publication-card__media--loading':
                      !isImageReady(publication.id) && !hasImageError(publication.id),
                    'publication-card__media--error': hasImageError(publication.id),
                  },
                ]"
              >
                <img
                  v-lazy="publication.thumbnail"
                  :alt="`${publication.title} thumbnail`"
                  loading="lazy"
                  :class="{ 'is-visible': isImageReady(publication.id) }"
                  @load="handleImageLoad(publication.id)"
                  @error="handleImageError(publication.id, $event)"
                />
                <span
                  v-if="hasImageError(publication.id)"
                  class="publication-card__media-error"
                >
                  {{ imageErrorText }}
                </span>
              </router-link>
              <div
                v-else
                class="publication-card__media publication-card__media--empty"
                aria-hidden="true"
              ></div>
            </div>
            <div class="publication-card__body">
              <h2 class="publication-card__title">
                <router-link
                  class="publication-card__title-link"
                  :to="publicationRoute(publication)"
                >
                  {{ publication.title }}
                </router-link>
              </h2>
              <p class="publication-card__authors">{{ publication.authors }}</p>
              <p class="publication-card__venue">
                {{ publication.venue }}
                <span v-if="publication.year"> - {{ publication.year }}</span>
              </p>
              <p v-if="publication.description" class="publication-card__description">
                {{ publication.description }}
              </p>
              <ul v-if="publication.topics?.length" class="publication-card__topics">
                <li v-for="topic in publication.topics" :key="topic">{{ topic }}</li>
              </ul>
              <div v-if="publication.links?.length" class="publication-card__actions">
                <a
                  v-for="link in publication.links"
                  :key="link.url"
                  :href="link.url"
                  target="_blank"
                  rel="noopener"
                  :class="['action-button', `action-button--${link.variant || 'primary'}`]"
                >
                  {{ link.label }}
                </a>
              </div>
            </div>
          </article>
        </div>
        <p v-if="!publicationGroups.length" class="empty-state">{{ emptyState }}</p>
      </div>
      <aside v-if="yearIndex.length" class="year-index">
        <button
          v-for="item in yearIndex"
          :key="item.year"
          type="button"
          class="year-index__item"
          :class="{ 'is-active': activeYear === item.year }"
          @click="scrollToYear(item.year)"
        >
          <span class="year-index__label">{{ item.year }}</span>
          <span class="year-index__count">{{ item.count }}</span>
        </button>
      </aside>
    </section>
  </div>
</template>

<script>
import { getLocaleBlock } from '@/utils/i18n';

const BADGE_STYLES = {
  default: {
    background: '#E6F0F8', // Corresponds to --primary-color-light
    color: '#004A99',      // Corresponds to --primary-color
    border: '#E6F0F8',      // Corresponds to --primary-color-light
  },
};

const FALLBACK_COVER =
  'https://laboratory.img.itchichi.de/publications/cover.png';

export default {
  name: 'PublicationsView',
  data() {
    return {
      filterMode: 'selected',
      activeTopics: [],
      activeYear: '',
      imageStates: {},
    };
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    this.$nextTick(() => {
      this.handleScroll();
    });
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  computed: {
    page() {
      return getLocaleBlock(this.$i18n, 'publicationsPage', {}) || {};
    },
    breadcrumb() {
      const data = this.page.breadcrumb || {};
      return {
        home: data.home || 'Home',
        current: data.current || 'Publications',
      };
    },
    intro() {
      const intro = this.page.intro || {};
      return {
        title: intro.title || 'Publications',
        description: intro.description || '',
      };
    },
    modes() {
      const modeList = this.page.filters?.modes;
      return Array.isArray(modeList) ? modeList : [];
    },
    topics() {
      const topicList = this.page.filters?.topics;
      return Array.isArray(topicList) ? topicList : [];
    },
    publications() {
      const list = this.page.publications;
      return Array.isArray(list) ? list : [];
    },
    emptyState() {
      return this.page.emptyState || 'No publications available.';
    },
    imageErrorText() {
      return this.page.imageError || 'Image unavailable';
    },
    filteredPublications() {
      let list = this.publications || [];

      if (this.filterMode === 'selected') {
        list = list.filter((item) => item.selected);
      }

      if (this.activeTopics.length) {
        list = list.filter((item) => {
          const category = item.category;
          if (category) {
            return this.activeTopics.includes(category);
          }
          return (
            Array.isArray(item.topics) &&
            item.topics.some((topic) => this.activeTopics.includes(topic))
          );
        });
      }

      const toTimeValue = (value) => {
        if (!value && value !== 0) return 0;
        if (typeof value === 'number') {
          // When a plain year (e.g., 2024) is provided use Jan 1st of that year.
          return value > 9999 ? value : new Date(value, 0, 1).getTime();
        }
        if (typeof value === 'string') {
          const normalized = value.trim().replace(/[./]/g, '-');
          if (!normalized) return 0;
          const [year, month = '1', day = '1'] = normalized.split('-');
          const y = parseInt(year, 10);
          if (Number.isNaN(y)) return 0;
          const m = parseInt(month, 10);
          const d = parseInt(day, 10);
          const safeMonth = Number.isNaN(m) ? 1 : Math.min(Math.max(m, 1), 12);
          const safeDay = Number.isNaN(d) ? 1 : Math.min(Math.max(d, 1), 31);
          return new Date(y, safeMonth - 1, safeDay).getTime();
        }
        return 0;
      };

      return [...list].sort((a, b) => {
        const diff = toTimeValue(b.year) - toTimeValue(a.year);
        if (diff !== 0) {
          return diff;
        }
        return (a.title || '').localeCompare(b.title || '');
      });
    },
    publicationGroups() {
      const groups = [];

      this.filteredPublications.forEach((publication) => {
        const yearLabel = this.normalizeYear(publication.year);
        const group = groups.find((item) => item.year === yearLabel);

        if (group) {
          group.items.push(publication);
        } else {
          groups.push({
            year: yearLabel,
            items: [publication],
          });
        }
      });

      return groups;
    },
    yearIndex() {
      return this.publicationGroups.map((group) => ({
        year: group.year,
        count: Array.isArray(group.items) ? group.items.length : 0,
      }));
    },
  },
  watch: {
    modes: {
      immediate: true,
      handler(modes) {
        if (!modes.length) {
          this.filterMode = 'all';
          return;
        }
        const hasCurrent = modes.some((mode) => mode.id === this.filterMode);
        this.filterMode = hasCurrent ? this.filterMode : modes[0].id;
      },
    },
    topics: {
      immediate: true,
      handler(topics) {
        const validTopics = (topics || []).filter(Boolean);
        const current = this.activeTopics.filter((topic) => validTopics.includes(topic));

        // Default to all available topics when none are active.
        this.activeTopics = current.length ? current : [...validTopics];
      },
    },
    publicationGroups: {
      immediate: true,
      handler(groups) {
        if (!groups.length) {
          this.activeYear = '';
          return;
        }
        if (!groups.some((group) => group.year === this.activeYear)) {
          this.activeYear = groups[0].year;
        }
      },
    },
  },
  methods: {
    setFilterMode(modeId) {
      this.filterMode = modeId;
    },
    toggleTopic(topic) {
      if (this.isTopicActive(topic)) {
        this.activeTopics = this.activeTopics.filter((item) => item !== topic);
      } else {
        this.activeTopics = [...this.activeTopics, topic];
      }
    },
    isTopicActive(topic) {
      return this.activeTopics.includes(topic);
    },
    badgeStyle(type) {
      const style = BADGE_STYLES[type] || BADGE_STYLES.default;
      return {
        backgroundColor: style.background,
        color: style.color,
        borderColor: style.border,
      };
    },
    normalizeYear(value) {
      const fallback = (this.page && this.page.unknownYear) || 'Other';
      if (value === 0) return '0';
      if (value === null || value === undefined) {
        return fallback;
      }
      if (typeof value === 'number') {
        return String(value);
      }
      if (typeof value === 'string') {
        const trimmed = value.trim();
        const match = trimmed.match(/^(\d{4})/);
        if (match) {
          return match[1];
        }
        const parsed = parseInt(trimmed, 10);
        if (!Number.isNaN(parsed)) {
          return String(parsed);
        }
      }
      return fallback;
    },
    publicationRoute(publication) {
      return {
        name: 'PublicationDetail',
        params: {
          id: publication.id,
        },
      };
    },
    isImageReady(id) {
      return this.imageStates[id] === 'loaded';
    },
    hasImageError(id) {
      return this.imageStates[id] === 'error';
    },
    handleImageLoad(id) {
      this.$set(this.imageStates, id, 'loaded');
    },
    handleImageError(id, event) {
      const target = event?.target;

      if (target && target.dataset.fallbackApplied !== 'true') {
        // Apply a default cover once to avoid infinite error loops.
        target.dataset.fallbackApplied = 'true';
        target.src = FALLBACK_COVER;
        return;
      }

      this.$set(this.imageStates, id, 'error');
    },
    yearAnchor(year) {
      return `year-${String(year).replace(/[^a-zA-Z0-9]/g, '-')}`;
    },
    scrollToYear(year) {
      const targetId = this.yearAnchor(year);
      const root = this.$el;

      if (!root) return;

      const target = root.querySelector(`#${targetId}`);

      if (!target) return;

      this.activeYear = year;

      const top = target.getBoundingClientRect().top + window.scrollY - 16;
      window.scrollTo({
        top,
        behavior: 'smooth',
      });
    },
    handleScroll() {
      const root = this.$el;
      if (!root) return;

      const sections = Array.from(root.querySelectorAll('.publication-year'));
      if (!sections.length) return;

      const scrollPos = window.scrollY + 140; // offset to align with sticky index
      let currentYear = sections[0].dataset.year || this.activeYear;

      for (const section of sections) {
        const top = section.getBoundingClientRect().top + window.scrollY;
        if (scrollPos >= top) {
          currentYear = section.dataset.year || currentYear;
        } else {
          break;
        }
      }

      if (currentYear && currentYear !== this.activeYear) {
        this.activeYear = currentYear;
      }
    },
  },
};
</script>

<style scoped>
.publications {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
  max-width: 1400px;
  margin-right: auto;
  margin-left: auto;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  font-size: var(--font-size-sm);
  color: var(--text-color-light);
}

.breadcrumb__link {
  color: var(--text-color-light);
  text-decoration: none;
  transition: color 0.2s ease;
}

.breadcrumb__link:hover {
  color: var(--primary-color);
}

.breadcrumb__separator {
  color: var(--border-color);
}

.breadcrumb__current {
  font-weight: var(--font-weight-bold);
  color: var(--text-color);
}

.page-header h1 {
  font-size: 2.1rem;
  color: var(--text-color);
  margin-bottom: var(--spacing-sm);
}

.page-header p {
  color: var(--text-color-light);
  line-height: 1.7;
  max-width: 680px;
}

.controls {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  align-items: flex-start;
}

.controls__modes {
  display: inline-flex;
  align-items: center;
  padding: var(--spacing-xs);
  border-radius: 999px;
  background: var(--background-color);
  border: 1px solid var(--border-color);
}

.mode-button {
  border: none;
  background: transparent;
  color: var(--text-color-light);
  padding: 0.45rem 1.25rem;
  border-radius: 999px;
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  transition: all 0.2s ease;
}

.mode-button--active {
  background: var(--primary-color);
  color: var(--text-color-on-primary);
  box-shadow: var(--box-shadow);
}

.mode-button:not(.mode-button--active):hover {
  background: var(--primary-color-light);
  color: var(--primary-color);
}

.controls__topics {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-sm);
}

.topic-chip {
  border: 1px solid var(--border-color);
  background: var(--surface-color);
  color: var(--text-color-light);
  font-weight: var(--font-weight-bold);
  border-radius: 999px;
  padding: 0.4rem 1.1rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.topic-chip--active {
  background: var(--primary-color-light);
  border-color: var(--primary-color);
  color: var(--primary-color-dark);
}

.topic-chip:not(.topic-chip--active):hover {
  border-color: var(--primary-color);
  color: var(--primary-color);
}

.publication-list {
  position: relative;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: start;
  gap: var(--spacing-lg);
}

.publication-list__content {
  display: flex;
  flex-direction: column;
  gap: 2.2rem;
}

.publication-year {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.publication-year__header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.35rem 0;
  color: var(--text-color);
}

.publication-year__bar {
  width: 8px;
  height: 22px;
  border-radius: 6px;
  background: var(--primary-color);
  box-shadow: 0 6px 18px rgba(12, 45, 87, 0.25);
}

.publication-year__title {
  margin: 0;
  font-size: 1rem;
  letter-spacing: 0.02em;
}

/* Reverted publication card style */
.publication-card {
  position: relative;
  display: grid;
  grid-template-columns: minmax(160px, 220px) 1fr;
  gap: 2rem;
  padding: 2.2rem 2.6rem;
  background: var(--surface-color);
  border: 1px solid #e2e7f3;
  border-radius: 28px;
  box-shadow: 0 24px 52px rgba(17, 41, 84, 0.08);
}

.publication-card__aside {
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  align-items: flex-start;
}

.publication-card__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  text-align: center;
  padding: 0.25rem 0.75rem;
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  border: 1px solid;
  line-height: 1.2;
  max-width: 100%;
}

.publication-card__media {
  width: 100%;
  display: block;
  text-decoration: none;
  border-radius: 18px;
  overflow: hidden;
  background: var(--background-color);
  border: 1px solid var(--border-color);
  aspect-ratio: 4 / 3;
  position: relative;
}

.publication-card__media img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: opacity 0.35s ease;
}

.publication-card__media img.is-visible {
  opacity: 1;
}

.publication-card__media--loading::after {
  content: '';
  position: absolute;
  inset: 0;
  margin: auto;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 3px solid var(--primary-color-light);
  border-top-color: var(--primary-color);
  animation: spin 0.9s linear infinite;
}

.publication-card__media-error {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-sm);
  text-align: center;
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-sm);
  color: var(--error-color);
  background: #fcf2f2;
}

.publication-card__media--empty {
  display: block;
  min-height: 180px;
  background: var(--background-color);
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.publication-card__body {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.publication-card__title {
  margin: 0;
  font-size: 1.35rem;
  line-height: 1.35;
  color: var(--text-color);
}

.publication-card__title-link {
  color: inherit;
  text-decoration: none;
  transition: color 0.2s ease;
}

.publication-card__title-link:hover {
  color: var(--primary-color);
}

.publication-card__authors {
  margin: 0;
  color: var(--text-color-light);
  font-size: 0.98rem;
}

.publication-card__venue {
  margin: 0;
  font-weight: var(--font-weight-bold);
  color: var(--text-color);
  font-size: 0.95rem;
}

.publication-card__description {
  margin: 0;
  color: var(--text-color-light);
  line-height: var(--line-height-base);
}

.publication-card__topics {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-sm);
  margin: 0;
  padding: 0;
  list-style: none;
}

.publication-card__topics li {
  background: var(--background-color);
  color: var(--text-color-light);
  border-radius: var(--border-radius);
  padding: 0.25rem 0.75rem;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  border: 1px solid var(--border-color);
}

.publication-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-sm);
}

.action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.55rem 1.5rem;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.95rem;
  letter-spacing: 0.02em;
  text-decoration: none;
  border: 1px solid transparent;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease,
    color 0.2s ease, border-color 0.2s ease;
}

.action-button:hover {
  transform: translateY(-1px);
}

.action-button:focus-visible {
  outline: 2px solid #f0b429;
  outline-offset: 2px;
}

.action-button--primary {
  background: #0c2d57;
  color: #f8fbff;
  border-color: #0c2d57;
  box-shadow: 0 12px 26px rgba(12, 45, 87, 0.28);
}

.action-button--primary:hover {
  background: #0a2446;
  border-color: #0a2446;
  box-shadow: 0 14px 30px rgba(10, 36, 70, 0.3);
}

.action-button--secondary {
  background: #f0b429;
  color: #2c1f00;
  border-color: #f0b429;
  box-shadow: 0 12px 26px rgba(240, 180, 41, 0.26);
}

.action-button--secondary:hover {
  background: #e5a200;
  border-color: #e5a200;
  color: #1f1400;
  box-shadow: 0 14px 30px rgba(229, 162, 0, 0.28);
}

.year-index {
  position: sticky;
  top: 110px;
  align-self: flex-start;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.75rem 0.65rem;
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  box-shadow: var(--box-shadow);
}

.year-index__item {
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  color: var(--text-color-light);
  font-weight: var(--font-weight-bold);
  padding: 0.35rem 0.8rem;
  border-radius: 999px;
  cursor: pointer;
  transition: color 0.2s ease, background-color 0.2s ease;
}

.year-index__item:hover {
  color: var(--primary-color);
}

.year-index__item.is-active {
  color: var(--primary-color);
  background: var(--primary-color-light);
}

.year-index__label {
  white-space: nowrap;
}

.year-index__count {
  min-width: 30px;
  padding: 0.12rem 0.5rem;
  text-align: center;
  font-size: 0.85rem;
  color: var(--text-color);
  background: var(--background-color);
  border-radius: 999px;
  border: 1px solid var(--border-color);
}

.empty-state {
  text-align: center;
  color: var(--text-color-light);
  padding: var(--spacing-xl) 0;
}

@media (min-width: 960px) {
  .controls {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
}

@media (max-width: 960px) {
  .publication-list {
    grid-template-columns: 1fr;
  }

  .year-index {
    display: none;
  }

  .publication-card {
    grid-template-columns: 1fr;
    padding: 1.9rem;
  }

  .publication-card__aside {
    flex-direction: row;
    align-items: center;
  }

  .publication-card__media {
    width: 160px;
    flex-shrink: 0;
  }
}

@media (max-width: 640px) {
  .page-header h1 {
    font-size: 1.75rem;
  }

  .publication-card {
    padding: 1.6rem;
  }

  .publication-card__aside {
    flex-direction: column;
    align-items: flex-start;
  }

  .publication-card__media {
    width: 100%;
  }

  .action-button {
    width: 100%;
    justify-content: center;
  }
}
</style>
